import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, normalizeStyle as _normalizeStyle, unref as _unref, renderList as _renderList, Fragment as _Fragment, normalizeClass as _normalizeClass, createVNode as _createVNode, withCtx as _withCtx } from "vue"

const _hoisted_1 = { class: "date-range-picker" }
const _hoisted_2 = { class: "date-range-picker__trigger-label" }
const _hoisted_3 = { class: "date-range-picker__trigger-value" }
const _hoisted_4 = { class: "date-range-picker__panel" }
const _hoisted_5 = {
  class: "date-range-picker__presets",
  role: "listbox",
  "aria-label": "Date range presets"
}
const _hoisted_6 = ["aria-selected", "onClick"]
const _hoisted_7 = { class: "date-range-picker__calendar-shell" }
const _hoisted_8 = { class: "date-range-picker__calendar-toolbar" }
const _hoisted_9 = { class: "date-range-picker__calendar-toolbar-title" }
const _hoisted_10 = { class: "date-range-picker__calendar-month-label" }
const _hoisted_11 = { class: "date-range-picker__calendar-year-select-wrap" }
const _hoisted_12 = { class: "date-range-picker__calendar-toolbar-actions" }
const _hoisted_13 = { class: "date-range-picker__footer" }
const _hoisted_14 = ["disabled"]

import {
    computed,
    nextTick,
    ref,
} from 'vue';
import DatePicker from 'primevue/datepicker';
import Popover from 'primevue/popover';
import Select from 'primevue/select';
import {
    cloneDateRangeValue,
    dateRangePresets,
    getMatchingPreset,
    getPresetRange,
    toCommittedRange,
    toDraftRange,
    type DateRangeDraft,
    type DateRangePresetId,
    type DateRangeValue,
} from '@/utils/datePickerPresets';
import {
    calendarDt as sharedCalendarDt,
    calendarPt as sharedCalendarPt,
} from '@/utils/datePickerTheme';
import { calendarToJSDate, todayIn } from '@/utils/dt';
import { useTimezoneStore } from '@/stores/timezone';

interface Props {
    modelValue: DateRangeValue
    label?: string
    triggerWidth?: string
    triggerRadius?: string
}

interface CalendarHeaderItem {
    key: string
    index: number
    month: number
    year: number
    monthLabel: string
}

interface DatePickerInstance {
    currentMonth: number | null
    currentYear: number | null
    currentView: string
    onMonthDropdownChange?: (value: string) => void
    onYearDropdownChange?: (value: string) => void
}

const VISIBLE_MONTH_COUNT = 2;
const YEAR_OPTIONS_BUFFER = 10;


export default /*@__PURE__*/_defineComponent({
  __name: 'DateRangePicker',
  props: {
    modelValue: {},
    label: { default: 'Date:' },
    triggerWidth: { default: '265px' },
    triggerRadius: { default: '20px' }
  },
  emits: ['update:modelValue'],
  setup(__props: any, { emit: __emit }) {

/* eslint-env vue/setup-compiler-macros */
const props = __props;

const emit = __emit;

const tzStore = useTimezoneStore();

const popoverRef = ref();
const datePickerRef = ref<DatePickerInstance | null>(null);
const draftRange = ref<DateRangeDraft>(toDraftRange(props.modelValue));

const dateFormatter = new Intl.DateTimeFormat('en-US', {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
});

const monthFormatter = new Intl.DateTimeFormat('en-US', {
    month: 'long',
});

const calendarDt = {
    ...sharedCalendarDt,
    panel: {
        padding: '0',
        background: 'transparent',
        borderColor: 'transparent',
        borderRadius: '0',
        color: '#344054',
        shadow: 'none',
    },
    header: {
        padding: '0 0 0.5rem',
        background: 'transparent',
        color: '#344054',
        borderColor: 'transparent',
    },
    group: {
        borderColor: 'transparent',
    },
};

const calendarPt = {
    ...sharedCalendarPt,
    root: {
        class: 'date-range-picker__calendar',
    },
    panel: {
        style: {
            display: 'flex',
            flexDirection: 'column',
            width: '100%',
            height: '100%',
            minWidth: '0',
            minHeight: '0',
            overflow: 'hidden',
            boxShadow: 'none',
        },
    },
    calendarContainer: {
        class: 'date-range-picker__calendar-container',
    },
    header: {
        class: 'date-range-picker__calendar-header',
        style: {
            display: 'none',
        },
    },
    title: {
        class: 'date-range-picker__calendar-title',
    },
    selectMonth: {
        disabled: false,
        style: {
            fontSize: '11px',
            cursor: 'pointer',
        },
    },
    selectYear: {
        disabled: false,
        style: {
            fontSize: '15px',
            cursor: 'pointer',
        },
    },
};

const yearSelectPt = {
    root: {
        class: 'date-range-picker__year-select',
        style: {
            display: 'inline-flex',
            alignItems: 'center',
            width: 'auto',
            minWidth: '0',
            minHeight: 'auto',
            padding: '0',
            border: 'none',
            background: 'transparent',
            boxShadow: 'none',
            gap: '0',
        },
    },
    label: {
        class: 'date-range-picker__year-select-label',
        style: {
            padding: '0',
            marginLeft: '3px',
            minHeight: 'auto',
            fontFamily: 'Archivo, sans-serif',
            fontSize: '15px',
            fontWeight: '500',
            lineHeight: '1',
            color: '#000000',
        },
    },
    dropdown: {
        class: 'date-range-picker__year-select-dropdown',
        style: {
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '10px',
            padding: '0',
            marginLeft: '3px',
            border: 'none',
            background: 'transparent',
            color: 'rgba(0, 0, 0, 0.55)',
        },
    },
    dropdownIcon: {
        class: 'date-range-picker__year-select-dropdown-icon',
        style: {
            fontSize: '6px',
        },
    },
    overlay: {
        class: 'date-range-picker__year-select-overlay',
        style: {
            marginTop: '4px',
            marginLeft: '-6px',
        },
    },
    list: {
        class: 'date-range-picker__year-select-list',
        style: {
            padding: '4px',
        },
    },
    option: {
        class: 'date-range-picker__year-select-option',
        style: {
            padding: '6px 4px',
            borderRadius: '8px',
            fontFamily: 'Archivo, sans-serif',
            fontSize: '15px',
            fontWeight: '500',
            lineHeight: '1',
            color: '#344054',
        },
    },
    optionLabel: {
        style: {
            fontFamily: 'Archivo, sans-serif',
            fontSize: '15px',
            fontWeight: '500',
            lineHeight: '1',
        },
    },
};

const displayValue = computed(() => {
    if (!props.modelValue) {
        return 'Select range';
    }

    return props.modelValue
        .map((date) => dateFormatter.format(date))
        .join(' - ');
});

const normalizedDraftRange = computed(() => toCommittedRange(draftRange.value));

// PrimeVue re-derives the visible month on every model change without emitting an event,
// so the toolbar reads its state directly instead of tracking a copy that silently desyncs.
const calendarBaseDate = computed(() => {
    const pickerInstance = datePickerRef.value;

    if (pickerInstance?.currentMonth != null && pickerInstance.currentYear != null) {
        return new Date(pickerInstance.currentYear, pickerInstance.currentMonth, 1);
    }

    const fallbackDate = normalizedDraftRange.value?.[0]
        ?? calendarToJSDate(todayIn(tzStore.effectiveTimezone));

    return new Date(fallbackDate.getFullYear(), fallbackDate.getMonth(), 1);
});

const visibleCalendars = computed<CalendarHeaderItem[]>(() => (
    Array.from({ length: VISIBLE_MONTH_COUNT }, (_, index) => {
        const visibleDate = new Date(
            calendarBaseDate.value.getFullYear(),
            calendarBaseDate.value.getMonth() + index,
            1,
        );

        return {
            key: `${visibleDate.getFullYear()}-${visibleDate.getMonth()}`,
            index,
            month: visibleDate.getMonth(),
            year: visibleDate.getFullYear(),
            monthLabel: monthFormatter.format(visibleDate),
        };
    })
));

const yearOptions = computed(() => {
    const visibleYears = visibleCalendars.value.map((calendar) => calendar.year);
    const currentYear = new Date().getFullYear();
    const startYear = Math.min(...visibleYears, currentYear) - YEAR_OPTIONS_BUFFER;
    const endYear = Math.max(...visibleYears, currentYear) + YEAR_OPTIONS_BUFFER;

    return Array.from(
        { length: endYear - startYear + 1 },
        (_, index) => startYear + index,
    );
});

const activePresetId = computed(() => (
    getMatchingPreset(normalizedDraftRange.value, tzStore.effectiveTimezone)
));

const isApplyDisabled = computed(() => !draftRange.value || draftRange.value.length === 0);

const syncDraftWithModel = () => {
    draftRange.value = toDraftRange(cloneDateRangeValue(props.modelValue));
};

const applyCalendarBaseDate = (baseDate: Date) => {
    const normalizedBaseDate = new Date(
        baseDate.getFullYear(),
        baseDate.getMonth(),
        1,
    );

    if (!datePickerRef.value) {
        return;
    }

    datePickerRef.value.currentView = 'date';
    datePickerRef.value.onYearDropdownChange?.(String(normalizedBaseDate.getFullYear()));
    datePickerRef.value.onMonthDropdownChange?.(String(normalizedBaseDate.getMonth()));
};

const shiftCalendarView = (monthDelta: number) => {
    applyCalendarBaseDate(new Date(
        calendarBaseDate.value.getFullYear(),
        calendarBaseDate.value.getMonth() + monthDelta,
        1,
    ));
};

const updateCalendarYear = (calendar: CalendarHeaderItem, value: number | string) => {
    const targetYear = Number(value);

    if (Number.isNaN(targetYear)) {
        return;
    }

    const selectedMonthDate = new Date(targetYear, calendar.month, 1);
    const nextBaseDate = new Date(
        selectedMonthDate.getFullYear(),
        selectedMonthDate.getMonth() - calendar.index,
        1,
    );

    const [startDate, endDate] = draftRange.value ?? [];

    if (startDate instanceof Date && endDate instanceof Date) {
        const yearDelta = targetYear - calendar.year;
        draftRange.value = [startDate, endDate].map((date) => (
            new Date(
                date.getFullYear() + yearDelta,
                date.getMonth(),
                date.getDate(),
            )
        ));
    }

    nextTick(() => applyCalendarBaseDate(nextBaseDate));
};

const togglePanel = (event: MouseEvent) => {
    syncDraftWithModel();
    popoverRef.value?.toggle(event);
};

// Selecting a date makes PrimeVue re-centre the view on the new range start; the clicked day
// is already visible, so keep the months the user is looking at instead.
const keepCalendarView = () => {
    const pinnedBaseDate = calendarBaseDate.value;

    nextTick(() => applyCalendarBaseDate(pinnedBaseDate));
};

const selectPreset = (presetId: DateRangePresetId) => {
    const range = getPresetRange(presetId, tzStore.effectiveTimezone);
    draftRange.value = [...range];
    nextTick(() => applyCalendarBaseDate(range[0]));
};

const clearDraftRange = () => {
    draftRange.value = null;
    emit('update:modelValue', null);
    popoverRef.value?.hide();
};

const applyDraftRange = () => {
    if (isApplyDisabled.value) {
        return;
    }

    emit('update:modelValue', normalizedDraftRange.value as DateRangeValue);
    popoverRef.value?.hide();
};

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("button", {
      class: "date-range-picker__trigger",
      style: _normalizeStyle({
                width: props.triggerWidth,
                minWidth: props.triggerWidth,
                borderRadius: props.triggerRadius,
            }),
      type: "button",
      "aria-label": "Filter by date range",
      "aria-haspopup": "dialog",
      onClick: togglePanel
    }, [
      _createElementVNode("span", _hoisted_2, _toDisplayString(props.label), 1),
      _createElementVNode("span", _hoisted_3, _toDisplayString(displayValue.value), 1),
      _cache[3] || (_cache[3] = _createElementVNode("svg", {
        class: "date-range-picker__trigger-chevron",
        width: "12",
        height: "7",
        viewBox: "0 0 12 7",
        fill: "none",
        "aria-hidden": "true"
      }, [
        _createElementVNode("path", {
          d: "M1 1L6 6L11 1",
          stroke: "currentColor",
          "stroke-width": "1.5",
          "stroke-linecap": "round",
          "stroke-linejoin": "round"
        })
      ], -1))
    ], 4),
    _createVNode(_unref(Popover), {
      ref_key: "popoverRef",
      ref: popoverRef,
      pt: {
                root: { class: 'date-range-picker-popover' },
                content: { class: 'date-range-picker-popover__content' },
            }
    }, {
      default: _withCtx(() => [
        _createElementVNode("div", _hoisted_4, [
          _createElementVNode("div", _hoisted_5, [
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(dateRangePresets), (preset) => {
              return (_openBlock(), _createElementBlock("button", {
                key: preset.id,
                class: _normalizeClass(["date-range-picker__preset", {
                            'date-range-picker__preset--active':
                                activePresetId.value === preset.id,
                        }]),
                type: "button",
                role: "option",
                "aria-selected": activePresetId.value === preset.id,
                onClick: ($event: any) => (selectPreset(preset.id))
              }, _toDisplayString(preset.label), 11, _hoisted_6))
            }), 128))
          ]),
          _createElementVNode("div", _hoisted_7, [
            _createElementVNode("div", _hoisted_8, [
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(visibleCalendars.value, (calendar) => {
                return (_openBlock(), _createElementBlock("div", {
                  key: calendar.key,
                  class: "date-range-picker__calendar-toolbar-group"
                }, [
                  _createElementVNode("div", _hoisted_9, [
                    _createElementVNode("span", _hoisted_10, _toDisplayString(calendar.monthLabel), 1),
                    _createElementVNode("div", _hoisted_11, [
                      _createVNode(_unref(Select), {
                        modelValue: calendar.year,
                        options: yearOptions.value,
                        size: "small",
                        appendTo: "body",
                        dropdownIcon: "pi pi-chevron-down",
                        highlightOnSelect: true,
                        checkmark: false,
                        pt: yearSelectPt,
                        "aria-label": "Select year",
                        "onUpdate:modelValue": ($event: any) => (updateCalendarYear(calendar, $event))
                      }, null, 8, ["modelValue", "options", "onUpdate:modelValue"])
                    ])
                  ]),
                  _createElementVNode("div", _hoisted_12, [
                    _createElementVNode("button", {
                      class: "date-range-picker__calendar-nav-button",
                      type: "button",
                      "aria-label": "Previous month",
                      onClick: _cache[0] || (_cache[0] = ($event: any) => (shiftCalendarView(-1)))
                    }, [...(_cache[4] || (_cache[4] = [
                      _createElementVNode("span", {
                        class: "pi pi-chevron-left date-range-picker__calendar-nav-icon",
                        "aria-hidden": "true"
                      }, null, -1)
                    ]))]),
                    _createElementVNode("button", {
                      class: "date-range-picker__calendar-nav-button",
                      type: "button",
                      "aria-label": "Next month",
                      onClick: _cache[1] || (_cache[1] = ($event: any) => (shiftCalendarView(1)))
                    }, [...(_cache[5] || (_cache[5] = [
                      _createElementVNode("span", {
                        class: "pi pi-chevron-right date-range-picker__calendar-nav-icon",
                        "aria-hidden": "true"
                      }, null, -1)
                    ]))])
                  ])
                ]))
              }), 128))
            ]),
            _createVNode(_unref(DatePicker), {
              ref_key: "datePickerRef",
              ref: datePickerRef,
              modelValue: draftRange.value,
              "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((draftRange).value = $event)),
              selectionMode: "range",
              inline: "",
              numberOfMonths: 2,
              manualInput: false,
              dt: calendarDt,
              pt: calendarPt,
              onDateSelect: keepCalendarView
            }, null, 8, ["modelValue"]),
            _createElementVNode("div", _hoisted_13, [
              _createElementVNode("button", {
                class: "date-range-picker__footer-action date-range-picker__footer-action--secondary",
                type: "button",
                onClick: clearDraftRange
              }, " Clear "),
              _createElementVNode("button", {
                class: "date-range-picker__footer-action date-range-picker__footer-action--primary",
                type: "button",
                disabled: isApplyDisabled.value,
                onClick: applyDraftRange
              }, " Done ", 8, _hoisted_14)
            ])
          ])
        ])
      ]),
      _: 1
    }, 512)
  ]))
}
}

})